
import { BaseButton,  Button, Checkbox, ContextualMenuItemType, DefaultButton, Dialog, DialogFooter, DialogType, Icon,  IContextualMenuItem, IContextualMenuProps,  Label, MessageBar, MessageBarType, PrimaryButton, Spinner, SpinnerSize, TextField } from 'office-ui-fabric-react';

import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { WsDecodeBoardInterface } from '../../../../../core/src/interfaces';
import { WsDecodeContext } from '../DecodeContext';
import { createInsightBoardInInvestigations, getProposedBoardMetrics } from './DecodeInsights.helpers';
import { createInsightUseCase, insightUseCaseDefaults } from './DecodeInsightsUseCase';




type DecodeInsightsDialogProps = {
  title: string;
  insightName: string;
  loadBoards: () => Promise<void>;
  // label?: string;
  // items: WsSalesSolutionItem[];
  // selectAction: boolean;
  close: () => void;
  // continue: (selectedItem: WsSalesSolutionItem | WsSalesSolutionAction | undefined) => void;
};

const DecodeInsightsDialog = (props: DecodeInsightsDialogProps) => {

  const history = useHistory();
  const decodeContext = useContext(WsDecodeContext);
  const isCreateBoard = props.title.startsWith('Create Board');
  const proposedMetrics = useMemo(() => getProposedBoardMetrics(props.insightName), [ props.insightName ]);
  const [ selectedMetricIds, setSelectedMetricIds ] = useState<string[]>(() => proposedMetrics.map((metric) => metric.id));
  const selectedMetrics = useMemo(() => proposedMetrics.filter((metric) => selectedMetricIds.includes(metric.id)), [ proposedMetrics, selectedMetricIds ]);
  const [ isCreatingBoard, setIsCreatingBoard ] = useState<boolean>(false);
  const [ isCreatingUseCase, setIsCreatingUseCase ] = useState<boolean>(false);
  const [ useCaseTitle, setUseCaseTitle ] = useState<string>('');
  const [ ideaDescription, setIdeaDescription ] = useState<string>('');
  const [ useCaseError, setUseCaseError ] = useState<string>('');
  const [ createdUseCase, setCreatedUseCase ] = useState<{ id: string | null } | null>(null);
  const isCreating = isCreatingBoard || isCreatingUseCase;
  // @agentsNote Lock handlers synchronously before React renders the busy state, so rapid clicks cannot start duplicate create attempts or change/dismiss the pending selection.
  const creationInProgress = useRef<boolean>(false);

  useEffect(() => {
    setSelectedMetricIds(proposedMetrics.map((metric) => metric.id));
  }, [ proposedMetrics ]);

  const updateMetricSelection = (metricId: string, selected: boolean): void => {
    if (creationInProgress.current) {
      return;
    }

    setSelectedMetricIds((currentSelectedMetricIds) => {
      if (selected) {
        return currentSelectedMetricIds.includes(metricId) ? currentSelectedMetricIds : [ ...currentSelectedMetricIds, metricId ];
      }

      return currentSelectedMetricIds.filter((selectedMetricId) => selectedMetricId !== metricId);
    });
  };

  // const solutionContext = useContext(WsSalesSolutionContext);

  // const [ selectedItem, setSelectedItem ] = useState<WsSalesSolutionItem | WsSalesSolutionAction | undefined>(undefined);



  // const menuProps: IContextualMenuProps = React.useMemo(() => ({
  //   shouldFocusOnMount: true,
  //   subMenuHoverDelay: 250,
  //   items: getMenuItems(props.items, props.selectAction, setSelectedItem),
  // }), [ props.items ]);



  const onDismiss = (event?: React.MouseEvent<HTMLButtonElement, MouseEvent> | undefined) => {
    if (!creationInProgress.current) {
      props.close();
    }
  };

  const onClickCancel = async (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | Button | HTMLSpanElement, MouseEvent>) => {
    onDismiss();
  };

  const onCreateUseCase = async (): Promise<void> => {
    if (!useCaseTitle.trim() || !ideaDescription.trim()) {
      setUseCaseError('Enter a title and idea description.');
      return;
    }

    creationInProgress.current = true;
    setIsCreatingUseCase(true);
    setUseCaseError('');

    try {
      const result = await createInsightUseCase({
        title: useCaseTitle.trim(),
        ideaDescription: ideaDescription.trim(),
      });
      // @agentsNote The response can contain only a job ID; never use that ID as the use-case route identifier.
      const useCaseId = result.useCase?.id;
      const normalizedUseCaseId = typeof useCaseId === 'string' || typeof useCaseId === 'number'
        ? `${useCaseId}`.trim()
        : '';
      setCreatedUseCase({ id: normalizedUseCaseId || null });
    } catch (error) {
      setUseCaseError(error instanceof Error && error.message.startsWith('Could not confirm')
        ? error.message
        : 'Failed to create use case. Please check AI Hub before trying again.');
    } finally {
      creationInProgress.current = false;
      setIsCreatingUseCase(false);
    }
  };

  const onOpenUseCase = (): void => {
    if (!createdUseCase?.id) {
      return;
    }

    const useCasePath = `/deliver/ai/use-cases/${encodeURIComponent(createdUseCase.id)}`;
    props.close();
    history.push(useCasePath);
  };

  const onOpenUseCaseList = (): void => {
    props.close();
    history.push('/deliver/ai/dashboard');
  };

  const onClickContinue = async (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement | BaseButton | Button | HTMLSpanElement, MouseEvent>) => {
    if (creationInProgress.current) {
      return;
    }

    if (!isCreateBoard) {
      await onCreateUseCase();
      return;
    }

    if (selectedMetrics.length === 0) {
      return;
    }

    creationInProgress.current = true;
    setIsCreatingBoard(true);
    let createdBoard: WsDecodeBoardInterface | null | undefined;

    try {
      createdBoard = await createInsightBoardInInvestigations(props.insightName, selectedMetrics);

      if (createdBoard && createdBoard.id) {
        await props.loadBoards();
        decodeContext?.setSelectedBoardGroupId('101');
      }
    } catch (error) {
      // @agentsNote Unexpected API failures leave the modal open; finally restores interaction. Notifications, retries, and rollback remain deferred.
      return;
    } finally {
      // @agentsNote Clear the busy state before closing, avoiding state updates after this React 16 dialog unmounts on completion.
      creationInProgress.current = false;
      setIsCreatingBoard(false);
    }

    if (createdBoard === null) {
      props.close();
      return;
    }

    if (!createdBoard || !createdBoard.id) {
      return;
    }

    props.close();
    history.push(`/deliver/decode/dashboard/${createdBoard.id}`);
  };



  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: createdUseCase ? 'Use case created' : `${props.title}`,
        showCloseButton: false,
        // @agentsNote The default positioned innerContent would confine the loader to the body; use the modal main as its containing block to cover the header and footer too.
        styles: { innerContent: { position: 'static' } },
      }}
      modalProps={{
        className: 'ws-select-item-dialog ws-dialog-select',
        isBlocking: isCreating,
        focusTrapZoneProps: { 'aria-busy': isCreating },
        styles: {
          main: { position: 'relative' },
        }
      }}
    >
      {
        isCreating
        &&
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'wait',
          }}
        >
          <Spinner size={SpinnerSize.large} label={isCreateBoard ? 'Creating board...' : 'Creating use case...'} />
        </div>
      }
      <Fragment>
        <div className="ws-select-item-form">
          <div className="columns">
            <div className="column">
              {
                createdUseCase
                ?
                <>
                  <p>Your use case has been submitted. Qualification will continue in the background.</p>
                  {!createdUseCase.id && (
                    <p>You can find the use case in AI Hub.</p>
                  )}
                </>
                : isCreateBoard
                ?
                <>
                  <Label>Suggested Metrics:</Label>
                  <div className="table-container">
                    <table className="table is-fullwidth" aria-label="Proposed board metrics">
                      <thead>
                        <tr>
                          <th scope="col" style={{ width: '80px' }}>Include</th>
                          <th scope="col">Metric</th>
                          <th scope="col">Threshold</th>
                        </tr>
                      </thead>
                      <tbody>
                        {
                          proposedMetrics.map((metric) => {
                            return (
                              <tr key={metric.id}>
                                <td>
                                  <Checkbox
                                    ariaLabel={`Include ${metric.name}`}
                                    checked={selectedMetricIds.includes(metric.id)}
                                    disabled={isCreatingBoard}
                                    onChange={(event, checked) => {
                                      updateMetricSelection(metric.id, !!checked);
                                    }}
                                  />
                                </td>
                                <td>{metric.name}</td>
                                <td>{metric.threshold}</td>
                              </tr>
                            );
                          })
                        }
                        {
                          proposedMetrics.length === 0
                          &&
                          <tr>
                            <td colSpan={3}>No proposed metrics configured for this insight.</td>
                          </tr>
                        }
                      </tbody>
                    </table>
                  </div>
                </>
                :
                <>
                  {useCaseError && <MessageBar messageBarType={MessageBarType.error}>{useCaseError}</MessageBar>}
                  <TextField
                    label="Title"
                    value={useCaseTitle}
                    onChange={(_event, value) => setUseCaseTitle(value || '')}
                    required
                    disabled={isCreatingUseCase}
                  />
                  <TextField
                    label="Idea description"
                    value={ideaDescription}
                    onChange={(_event, value) => setIdeaDescription(value || '')}
                    multiline
                    rows={4}
                    required
                    disabled={isCreatingUseCase}
                  />
                  <TextField label="Area" value={insightUseCaseDefaults.area} readOnly />
                  <TextField label="Client group" value={insightUseCaseDefaults.clientGroup} readOnly />
                  <TextField label="Business function" value={insightUseCaseDefaults.businessFunction} readOnly />
                </>
              }
              {/* {
                props.label
                &&
                <>
                  <Label>{props.label}</Label>
                </>
              } */}
              {/* <DefaultButton
                text={getDependencyText(props.items, selectedItem ? selectedItem.id : undefined, props.selectAction ? 'Select Action' : 'Select Capability', props.selectAction)}
                menuProps={menuProps}
              /> */}
              <br />
            </div>
          </div>
        </div>
      </Fragment>
      <DialogFooter>
        {createdUseCase ? (
          <>
            <PrimaryButton onClick={onOpenUseCase} text="Show Use-Case Item" disabled={!createdUseCase.id} />
            <DefaultButton onClick={onOpenUseCaseList} text="Show Use-Case List" style={{ marginLeft: '4px' }} />
            <DefaultButton onClick={onClickCancel} text="Continue" style={{ marginLeft: '4px' }} />
          </>
        ) : (
          <>
            <DefaultButton onClick={onClickCancel}   text="Cancel" disabled={isCreating} />
            <PrimaryButton onClick={onClickContinue} text="Create" disabled={isCreating || (isCreateBoard && selectedMetrics.length === 0)} style={{ marginLeft: '4px' }} />
          </>
        )}
      </DialogFooter>
    </Dialog>
  );
};




export {
  DecodeInsightsDialog
};
